import type { ClientSession } from "../types/common";

export const CLIENT_STORAGE_KEY = "dialin24_client_user";
export const CLIENT_AUTH_CHANGE_EVENT = "dialin24-client-auth-change";

type StoredClientSession = Partial<ClientSession> | null;

const toTrimmedString = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

const normalizeEmail = (value: unknown) => toTrimmedString(value).toLowerCase();

export const normalizeStoredClientSession = (
  clientUser: StoredClientSession
): ClientSession | null => {
  if (!clientUser) {
    return null;
  }

  const token = toTrimmedString(clientUser.token);
  const email = normalizeEmail(clientUser.email);

  if (!token && !email) {
    return null;
  }

  return {
    token,
    tokenExpires: toTrimmedString(clientUser.tokenExpires) || undefined,
    id: toTrimmedString(clientUser.id) || email || "client",
    name: toTrimmedString(clientUser.name) || "Client User",
    email,
    role: toTrimmedString(clientUser.role) || "Client",
    phone: toTrimmedString(clientUser.phone),
    city: toTrimmedString(clientUser.city) || undefined,
  };
};

export const getStoredClientUser = (): ClientSession | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const rawClientUser = window.localStorage.getItem(CLIENT_STORAGE_KEY);
    if (!rawClientUser) {
      return null;
    }

    const parsedClientUser = JSON.parse(rawClientUser) as StoredClientSession;
    return normalizeStoredClientSession(parsedClientUser);
  } catch {
    return null;
  }
};

export const getStoredClientToken = (): string | null =>
  getStoredClientUser()?.token || null;

const notifyAuthChange = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new Event(CLIENT_AUTH_CHANGE_EVENT));
};

export const saveStoredClientUser = (clientUser: ClientSession) => {
  if (typeof window === "undefined") {
    return;
  }

  const normalizedClientUser = normalizeStoredClientSession(clientUser);
  if (!normalizedClientUser) {
    return;
  }

  window.localStorage.setItem(
    CLIENT_STORAGE_KEY,
    JSON.stringify(normalizedClientUser)
  );
  notifyAuthChange();
};

export const clearStoredClientUser = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(CLIENT_STORAGE_KEY);
  notifyAuthChange();
};

export const subscribeToClientAuth = (callback: () => void) => {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  const handleChange = () => callback();
  window.addEventListener(CLIENT_AUTH_CHANGE_EVENT, handleChange);
  window.addEventListener("storage", handleChange);

  return () => {
    window.removeEventListener(CLIENT_AUTH_CHANGE_EVENT, handleChange);
    window.removeEventListener("storage", handleChange);
  };
};
